import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  BatchFilesSubmitPageContextType,
  useBatchFilesSubmitPageContext,
} from '../../../../BatchFilesSubmitPage.context';
import { normalizeText } from 'src/app/modules/OpmPd/utils/commonHelper';
import { QualifiedAgent } from '../../PolicyTable';

type AssignAgentButtonProps = {
  qualifiedAgents?: QualifiedAgent[];
  filteredItems?: BatchFilesSubmitPageContextType['formPolicies'];
};

const AssignAgentButton = (props: AssignAgentButtonProps) => {
  const { qualifiedAgents = [], filteredItems = [] } = props;
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { addOtherQualifiedAgentToPolicy } = useBatchFilesSubmitPageContext();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [selectedAgentCodes, setSelectedAgentCodes] = useState<string[]>([]);

  const filteredAgents = useMemo(() => {
    const normalizedKeyword = normalizeText(keyword);
    return qualifiedAgents.filter(
      (agent) =>
        normalizeText(agent.agentCode).includes(normalizedKeyword) ||
        normalizeText(agent.fullname).includes(normalizedKeyword),
    );
  }, [keyword, qualifiedAgents]);

  const handleOpen = () => {
    setKeyword('');
    setSelectedAgentCodes([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = (agentCode: string) => {
    setSelectedAgentCodes((prev) =>
      prev.includes(agentCode) ? prev.filter((c) => c !== agentCode) : [...prev, agentCode],
    );
  };

  const handleConfirm = () => {
    const filteredPolicyNumbers = new Set(filteredItems.map((item) => item.policyNumber));
    const selectedAgents = qualifiedAgents
      .filter((agent) => selectedAgentCodes.includes(agent.agentCode))
      .map((agent) => ({
        ...agent,
        policyNumbers: agent.policyNumbers.filter((policyNumber) => filteredPolicyNumbers.has(policyNumber)),
      }));
    addOtherQualifiedAgentToPolicy(selectedAgents);
    setOpen(false);
  };

  return (
    <>
      <Button variant="outlined" color="primary" onClick={handleOpen} sx={{ whiteSpace: 'nowrap' }}>
        {Translation('opm_pd.assignQualifiedAgentToPolicies')}
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {Translation('opm_pd.assignQualifiedAgentToPolicies')}
            <IconButton edge="end" size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogTitle sx={{ py: 0 }}>
          <OutlinedInput
            fullWidth
            size="small"
            placeholder={Translation('opm_pd.search')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </DialogTitle>
        <List component={DialogContent} sx={{ pt: 0 }}>
          {filteredAgents.length > 0 && (
            <ListItem disablePadding>
              <ListItemButton
                sx={{ px: 3 }}
                onClick={() => {
                  if (filteredAgents.every((a) => selectedAgentCodes.includes(a.agentCode))) {
                    setSelectedAgentCodes((prev) => prev.filter((c) => !filteredAgents.some((a) => a.agentCode === c)));
                  } else {
                    setSelectedAgentCodes((prev) => [
                      ...prev,
                      ...filteredAgents.filter((a) => !prev.includes(a.agentCode)).map((a) => a.agentCode),
                    ]);
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={
                      filteredAgents.length > 0 && filteredAgents.every((a) => selectedAgentCodes.includes(a.agentCode))
                    }
                    indeterminate={
                      filteredAgents.some((a) => selectedAgentCodes.includes(a.agentCode)) &&
                      !filteredAgents.every((a) => selectedAgentCodes.includes(a.agentCode))
                    }
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={Translation('opm_pd.selectAll')} />
              </ListItemButton>
            </ListItem>
          )}
          {filteredAgents.map((agent) => (
            <ListItem key={agent.agentCode} disablePadding>
              <ListItemButton sx={{ px: 3 }} onClick={() => handleToggle(agent.agentCode)}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={selectedAgentCodes.includes(agent.agentCode)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={`${agent.agentCode} - ${agent.fullname}`} />
              </ListItemButton>
            </ListItem>
          ))}
          {filteredAgents.length === 0 && (
            <ListItem sx={{ px: 3 }}>
              <ListItemText
                primary={Translation('opm_pd.noAgentsFound')}
                primaryTypographyProps={{ align: 'center' }}
                sx={{ color: 'text.secondary' }}
              />
            </ListItem>
          )}
        </List>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleConfirm}
            disabled={selectedAgentCodes.length === 0}
          >
            {Translation('opm_pd.confirm')}
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            {Translation('opm_pd.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignAgentButton;
